import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchIssues } from '../../../../../../redux/slices/issueSlice';
import { usePostBoxState } from './state';
import { usePostBoxHandlers } from './handlers';

/**
 * Main hook combining state and handlers for PostBoxModal
 */
export const usePostBoxForm = ({ open, onClose, userData }) => {
  const dispatch = useDispatch();
  const state = usePostBoxState();
  const handlers = usePostBoxHandlers(state, dispatch, onClose, userData);

  const { step, content, link, linkPreview, setIssueSearchTerm } = state;
  const { debouncedFetchPreview } = handlers;

  // Redux selectors
  const locations = useSelector(s => s.issues.locations);
  const issuesFromRedux = useSelector(s => s.issues.filteredIssues);
  const isLoadingIssues = useSelector(s => s.issues.loading);

  // Fetch issues when modal opens or step changes to 2
  useEffect(() => {
    if (open && step === 2) {
      dispatch(fetchIssues());
    }
    if (!open || step !== 2) {
      setIssueSearchTerm('');
    }
  }, [open, step, dispatch, setIssueSearchTerm]);

  // URL detection effect
  useEffect(() => {
    if (!content || step !== 4) return;
    if (linkPreview || link) return;

    const urlRegex = /((https?:\/\/|www\.)[^\s]+)/gi;
    const matches = content.match(urlRegex);

    if (matches && matches[0]) {
      const detectedUrl = matches[0].startsWith('www.')
        ? 'https://' + matches[0]
        : matches[0];
      debouncedFetchPreview(detectedUrl);
    }
  }, [content, step, linkPreview, link, debouncedFetchPreview]);

  // Fetch preview when manual link added
  useEffect(() => {
    if (link && !linkPreview) {
      debouncedFetchPreview(link);
    }
  }, [link, linkPreview, debouncedFetchPreview]);

  return {
    // State
    ...state,
    locations,
    issuesFromRedux,
    isLoadingIssues,

    // Handlers
    ...handlers,
  };
};

export default usePostBoxForm;